import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import moment from 'moment';
import { clsx } from 'clsx';
// Components
import { TextField } from '@mui/material';
// Resources
import { stringsRes } from '../../resources/strings';

interface DatePickerProps {
    iconElement?: JSX.Element;
    selectedDate: any;
    onChangeDateTime: (date?: any) => any;
    setFocusClone?: any;
    fullWidth?: boolean;
    isTimeFocus?: boolean;
    isError?: boolean;
    noError?: boolean;
    disabled?: boolean;
    disablePast?: boolean;
    limitDate?: boolean; // to set minDate to now and maxDate to 1 year later
    minDate?: Date;
    maxDate?: Date;
    className?: string;
    onFocus?: () => any;
    label?: string;
    id?: string;
}

const DatePicker: FC<DatePickerProps> = (props): ReactElement => {
    const {
        selectedDate,
        onChangeDateTime,
        setFocusClone = () => {},
        onFocus = () => {},
        isError = false,
        noError = false,
        fullWidth = false,
        isTimeFocus = false,
        disabled = false,
        disablePast = true,
        limitDate = false,
        minDate = limitDate ? moment().toDate() : null,
        maxDate = limitDate ? moment().add(1, 'year').toDate() : null,
        className = '',
        label,
        id = 'textfield0',
    } = props;
    const classes = useStyles();
    const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
    const [focus, setFocus] = useState<boolean>(false);
    const validDate = useRef(selectedDate);
    const textfieldRed = useRef(null);

    useEffect(() => {
        validDate.current = selectedDate?.isValid() ? selectedDate : validDate.current;
    }, [selectedDate]);

    const onOpenModal = (): void => {
        setIsDateModalOpen(true);
        setFocusClone(true);
        setFocus(true);
    };

    const onCloseModal = (): void => {
        setIsDateModalOpen(false);
        if (document.activeElement !== textfieldRed.current) {
            setFocusClone(false);
            setFocus(false);
        }
    };

    const onBlurAction = (): void => {
        if (!isDateModalOpen) {
            setFocusClone(false);
            setFocus(false);
        }
    };

    const onChangeDatePicker = (date: any): void => {
        const dateDuplicate = moment(date);
        dateDuplicate.set({
            h: validDate.current?.hours(),
        });
        dateDuplicate.set({
            m: validDate.current?.minutes(),
        });
        onChangeDateTime(dateDuplicate);
    };

    const showError = !noError && (isError || (!selectedDate?.isValid() && !focus && !isTimeFocus));
    return (
        <div className={className}>
            <DesktopDatePicker
                onOpen={onOpenModal}
                onClose={onCloseModal}
                open={isDateModalOpen}
                disabled={disabled}
                disablePast={disablePast}
                minDate={minDate}
                maxDate={maxDate}
                inputRef={textfieldRed}
                className={clsx(fullWidth && classes.pickerFull, focus ? classes.pickerFocus : classes.picker)}
                label={label ?? stringsRes.booking.date}
                value={selectedDate}
                onChange={onChangeDatePicker}
                PopperProps={{ className: classes.muiSelection, placement: 'bottom-start' }}
                InputProps={{
                    disableUnderline: true,
                    onBlur: onBlurAction,
                    onClick: () => {
                        textfieldRed.current.focus();
                        setFocus(true);
                        setFocusClone(true);
                    },
                    className: clsx(
                        focus ? classes.pickerFocus : classes.picker,
                        focus ? classes.pickerBorder : classes.pickerBorderWhite,
                        showError && classes.pickerErrorBorder,
                    ),
                }}
                renderInput={(params) => (
                    <TextField
                        variant="filled"
                        fullWidth
                        id={id}
                        onFocus={onFocus}
                        InputLabelProps={{
                            className: clsx(classes.label, focus && classes.labelBlue, showError && classes.labelError),
                        }}
                        className={clsx(classes.dateText, focus ? classes.pickerFocus : classes.picker)}
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            placeholder: stringsRes.time.placeholder,
                        }}
                    />
                )}
            />
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        picker: {
            background: `${theme.palette.colors.background.filledForm}!important`,
            borderRadius: '6px',
            minWidth: 160,
            '& .MuiInputBase-root': {
                fontWeight: 500,
            },
        },
        pickerFull: {
            width: '100%',
        },
        pickerFocus: {
            background: `${theme.palette.colors.background.secondaryBtn}!important`,
            borderRadius: '6px',
            '& .MuiInputBase-root': {
                fontWeight: 500,
            },
        },
        dateText: {
            height: 56,
            width: '100%',
            fontSize: '16px',
            fontWeight: '500',
        },
        pickerBorder: {
            border: `1.5px solid ${theme.palette.secondary.main}`,
        },
        pickerBorderWhite: {
            border: `1.5px solid transparent`,
        },
        label: {
            zIndex: 1,
            fontWeight: 500,
            color: `${theme.palette.primary.main}99!important`,
        },
        labelBlue: {
            color: `${theme.palette.secondary.main}!important`,
        },
        labelError: {
            color: 'red!important',
        },
        pickerErrorBorder: {
            border: `1.5px solid red!important`,
        },
        muiSelection: {
            '& .MuiButtonBase-root': {
                background: `${theme.palette.colors.background.secondaryBtn}!important`,
            },
            '& .MuiButtonBase-root:hover': {
                background: `${theme.palette.colors.border}!important`,
            },
            '& .MuiCalendarPicker-root': {
                background: `${theme.palette.colors.background.secondaryBtn}!important`,
                borderRadius: 6,
            },
            '& .Mui-selected': {
                backgroundColor: `${theme.palette.secondary.main}!important`,
            },
            '& .MuiPickersCalendarHeader-label': {
                marginRight: 0,
            },
        },
    }),
);

export default DatePicker;
